import {useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {DynamicComponentNames} from 'constants/client';
import {ClientContext, UserContext} from 'context';
import {useObservable} from 'hooks';
import {AsyncRequestStatusName, AsyncRequestTypeName, ClientSession, ParticipantAsyncRequest, SessionStatus} from 'interfaces';
import {ClientDynamicLayout} from 'layouts';
import {TRANSLATION_KEYS} from 'locale';
import {
    AgentsUnavailableView, RequestAppointmentView, RequestAppointmentSubmitted, CallingView, OnHoldView,
    SessionEndedView, FeedbackView, FeedbackSubmitted, WaitingForAgentView, AfterHoursView,
    SipView, AvatarView, FullScreenAsyncRequest
} from 'pages/Client';
import {ClientAppearanceFeatures, ClientView, SessionActionsContextProps} from 'pages/Client/types';
import {authDataStore} from 'services';
import {ClientSettings, ParticipantRole} from 'types';

import {ClientJoinSessionPagePreview, PreSessionFlowPreview, TextChatPreview} from './PreviewComponents';
import {whiteLabelPreviewEvents} from './WhiteLabelPreviewEvents';
import {WHITE_LABEL_PREVIEW_SESSION_PARAMS} from './WhiteLabelPreviewSessionParams';

type Props = {
    template: string;
    styles: string;
    componentName: DynamicComponentNames;
    clientSettings: ClientSettings;
};

const T_KEYS = TRANSLATION_KEYS.USER.CONTENT.CLIENT_SESSION.WHITE_LABEL.PREVIEW_MODAL;
const T_ELEMENT_CLICKED = T_KEYS.EVENTS.ELEMENT_CLICKED;

const getSession: ((User) => ClientSession) = (agent) => ({
    id: WHITE_LABEL_PREVIEW_SESSION_PARAMS.id,
    startDate: new Date(),
    status: SessionStatus.InSession,
    assignment: {
        accountId: agent.accountId
    },
    statusDate: new Date(),
    participants: [
        {
            data: {...agent},
            id: agent.id,
            role: ParticipantRole.Agent,
            socket: {
                connected: false,
                date: new Date()
            },
            asyncRequests: []
        }
    ],
    localParticipant: {
        ...WHITE_LABEL_PREVIEW_SESSION_PARAMS.client,
        asyncRequests: [],
        role: ParticipantRole.Client,
        socket: {
            connected: false,
            date: new Date()
        },
    },
    activities: [],
    possibleActivities: [],
    unsupportedActivities: [],
    conflictingActivities: null,
    screenshots: [],
    result: {},
    version: WHITE_LABEL_PREVIEW_SESSION_PARAMS.version,
    inviteClientParams: null,
    answered: true,
    prevActivities: []
});

const getSessionActionsContextProps: (TFunction) => SessionActionsContextProps = (t) => ({
    socket: {} as any,
    audioEnabled: true,
    videoEnabled: true,
    fullScreenVideo: true,
    videoDevices: null,
    audioDevices: null,
    videoConstraints: null,
    textChatThreads: null,
    textChatInteractiveChatState: null,
    avatarThreads: null,
    participantsNetworkQuality: null,
    untreatedAsyncRequests: null,
    setTreatedAsyncRequest: null,
    pictureInPictureActive: false,
    videoPinParticipant: null,
    twilioDevice: null,
    tracks: [],
    onToggleAudio: whiteLabelPreviewEvents.empty,
    onToggleVideo: whiteLabelPreviewEvents.empty,
    setScreenSharingActivityStatus: whiteLabelPreviewEvents.empty,
    setVideoConstraints: whiteLabelPreviewEvents.empty,
    onToggleFullScreenVideo: whiteLabelPreviewEvents.empty,
    onLocalNetworkQualityLevelChange: whiteLabelPreviewEvents.empty,
    onRemoteNetworkQualityLevelChange: whiteLabelPreviewEvents.empty,
    setMediaDevices: whiteLabelPreviewEvents.empty,
    onAudioStreamingToggle: whiteLabelPreviewEvents.empty,
    notifyWebRTCStartSuccess: whiteLabelPreviewEvents.empty,
    handleVideoPinParticipantUpdate: whiteLabelPreviewEvents.empty,
    notifyWebRTCStartFailed: whiteLabelPreviewEvents.empty,
    endLocalSession: () => whiteLabelPreviewEvents.mock(t(T_ELEMENT_CLICKED)),
    handleUploadSelfieVerificationResult: whiteLabelPreviewEvents.empty,
    executeUploadSelfieVerification: whiteLabelPreviewEvents.empty,
    addFeedback: () => whiteLabelPreviewEvents.mockAsync(t(T_ELEMENT_CLICKED)),
    requestAppointment: () => whiteLabelPreviewEvents.mockAsync(t(T_ELEMENT_CLICKED)),
    updateParticipantAsyncRequest: () => whiteLabelPreviewEvents.emptyAsync(),
    onFileDownload: () => whiteLabelPreviewEvents.mockAsync(t(T_ELEMENT_CLICKED)),
    onSubmitMessage: () => whiteLabelPreviewEvents.mockAsync(t(T_ELEMENT_CLICKED)),
    onChangeTextChatActivityState: () => whiteLabelPreviewEvents.emptyAsync(),
    onTogglePictureInPicture: whiteLabelPreviewEvents.empty,
    setTwilioDevice: whiteLabelPreviewEvents.empty,
    initTwilioDevice: whiteLabelPreviewEvents.empty,
    setTracks: whiteLabelPreviewEvents.empty,
});

const SESSION_ACTIONS_CONTAINER_CONTEXT_PROPS = {
    currentView: ClientView.Calling,
    isDevelopmentMode: false,
    conversationOnly: false,
    errorMessageTKey: null,
    waitingStartSessionFromChildren: false,
    device: null,
    features: ClientAppearanceFeatures.All,
    startSessionWithParams: whiteLabelPreviewEvents.empty,
    startSessionConnect: whiteLabelPreviewEvents.empty
};

const ASSIGNMENT_STATE = {
    agentsAvailable: true
};

function replaceTemplateAndStyles(clientSettings: ClientSettings, name: string, template: string, styles: string): ClientSettings {
    if (!clientSettings) {
        return null;
    }

    const originComponents = clientSettings.whiteLabel?.components || [];
    const components = [...originComponents.filter((component) => component.name !== name), {
        name,
        template,
        styles
    }];

    return {
        ...clientSettings,
        workingHours: {
            nextAvailableDate: new Date().toISOString()
        },
        whiteLabel: {
            components,
            enabled: clientSettings.whiteLabel?.enabled
        }
    };
}

export const WhiteLabelPreview: React.FC<Props> = ({componentName, template, styles, clientSettings}) => {
    const {t} = useTranslation();
    const {localUser} = useObservable(authDataStore);
    const {settings: {common}} = useContext(UserContext);
    const session = useMemo(() => getSession(localUser), [localUser]);

    const clientContext = useMemo(() => {
        const sessionForContext = componentName === DynamicComponentNames.FullScreenAsyncRequest
            ? {
                ...session,
                localParticipant: {
                    ...session.localParticipant,
                    asyncRequests: [{
                        type: AsyncRequestTypeName.SumaDocumentVerificationSDK,
                        status: {name: AsyncRequestStatusName.InProgress, date: new Date()},
                        statusHistory: [],
                        data: {
                            url: 'https://example.com'
                        }
                    } as ParticipantAsyncRequest]
                }
            }
            : session;

        return {
            session: sessionForContext,
            token: WHITE_LABEL_PREVIEW_SESSION_PARAMS.token,
            assignmentState: ASSIGNMENT_STATE,
            settings: replaceTemplateAndStyles({...clientSettings, common}, componentName, template, styles),
            ...getSessionActionsContextProps(t),
            ...SESSION_ACTIONS_CONTAINER_CONTEXT_PROPS
        };
    }, [t, clientSettings, componentName, template, styles, session, common]);

    return (
        <div className='tw-flex tw-justify-center'>
            <ClientContext.Provider value={clientContext}>
                {componentName === DynamicComponentNames.AfterHours && <AfterHoursView />}
                {componentName === DynamicComponentNames.AgentsUnavailable && <AgentsUnavailableView />}
                {componentName === DynamicComponentNames.Calling && <CallingView />}
                {componentName === DynamicComponentNames.Feedback && <FeedbackView onClose={() => whiteLabelPreviewEvents.empty()} />}
                {componentName === DynamicComponentNames.FeedbackSubmitted && <FeedbackSubmitted />}
                {componentName === DynamicComponentNames.OnHold && <OnHoldView />}
                {componentName === DynamicComponentNames.RequestAppointment &&
                    <RequestAppointmentView onCloseForm={() => whiteLabelPreviewEvents.empty()} />}
                {componentName === DynamicComponentNames.RequestAppointmentSubmitted && <RequestAppointmentSubmitted />}
                {componentName === DynamicComponentNames.SessionEnded && <SessionEndedView />}
                {componentName === DynamicComponentNames.Avatar && <AvatarView />}
                {componentName === DynamicComponentNames.WaitingForAgent && <WaitingForAgentView />}
                {componentName === DynamicComponentNames.Layout &&
                    <ClientDynamicLayout image={clientContext?.settings?.image?.path}>
                        <div>{t(T_KEYS.LAYOUT_SAMPLE_CONTENT)}</div>
                    </ClientDynamicLayout>}
                {componentName === DynamicComponentNames.JoinSession && <ClientJoinSessionPagePreview />}
                {(componentName === DynamicComponentNames.FullScreenTextChat || componentName === DynamicComponentNames.TextChat) &&
                    <TextChatPreview componentName={componentName} />}
                {componentName === DynamicComponentNames.SIP && <SipView />}
                {componentName === DynamicComponentNames.FullScreenAsyncRequest && <FullScreenAsyncRequest />}
                {componentName === DynamicComponentNames.PreSessionFlow && <PreSessionFlowPreview />}
            </ClientContext.Provider>
        </div>
    );
};
